import { useParams } from '@tanstack/react-router';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { isUsRegion } from '@/jurisdictions/regions';

/**
 * Americanization: US matters use the `/binders` URL prefix; every other region
 * uses `/bundles`. Both route trees render the same pages, so callers pick a
 * `base` and read the matching typed route paths from `bundlePaths`.
 */
export type BundleBase = 'bundles' | 'binders';

export function bundleBaseForRegion(region?: string | null): BundleBase {
  return isUsRegion(region) ? 'binders' : 'bundles';
}

/**
 * Typed route paths for the chosen prefix. `bundles` and `binders` share the
 * same params (`{ slug }`, `{ slug, bundleId }`), so callers branch only the
 * `to` string and reuse one `params` object — keeping TanStack's navigation
 * fully type-checked with no casts:
 *
 *   navigate({ to: bundlePaths(base).detail, params: { slug, bundleId } })
 *   <Link to={bundlePaths(base).index} params={{ slug }} />
 */
export function bundlePaths(base: BundleBase) {
  return base === 'binders'
    ? ({
        index: '/matter/$slug/binders',
        detail: '/matter/$slug/binders/$bundleId',
        edit: '/matter/$slug/binders/$bundleId/edit',
      } as const)
    : ({
        index: '/matter/$slug/bundles',
        detail: '/matter/$slug/bundles/$bundleId',
        edit: '/matter/$slug/bundles/$bundleId/edit',
      } as const);
}

/** If the main screen is a bundle viewer or editor, return that bundle id. */
export function useOpenBundleId(): string | null {
  const { bundleId } = useParams({ strict: false });
  return bundleId ?? null;
}

/** Resolve the bundle/binder paths from the current matter's region. */
export function useRegionBundlePaths() {
  const { region } = useMatterJurisdiction();
  return bundlePaths(bundleBaseForRegion(region));
}
